import { AppId } from '@wix/cashier-common/dist/src/enums/AppId';
import { CreditCardNetwork } from '@wix/cashier-common/dist/src/enums/payments/CreditCardNetwork';
import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { PaymentProvider } from '@wix/cashier-common/dist/src/enums/payments/PaymentProvider';
import { ServerTransactionStatus } from '@wix/cashier-common/dist/src/enums/transactions/ServerTransactionStatus';
import { RevalidateAppInstance } from '@wix/cashier-common/dist/src/types/AppInstance';
import { AppInstanceResolver } from '@wix/cashier-common/dist/src/utils/AppInstanceResolver';
import { alpha3ToAlpha2 } from '@wix/cashier-common/dist/src/utils/countryCodes';
import { experimentsService } from '@wix/cashier-common/dist/src/utils/experimentsService';
import { isRTLLocale } from '@wix/cashier-common/dist/src/utils/isRTLLocale';
import { timedOut } from '@wix/cashier-common/dist/src/utils/timedOut';
import { CreditCardService } from '@wix/cashier-core/dist/src/services/CreditCard';
import { IPublicCreditCard } from '@wix/cashier-core/dist/src/services/CreditCard/types';
import {
  IMandatoryField,
  MandatoryFieldDomain,
} from '@wix/cashier-core/dist/src/services/MandatoryFields/types';
import { PaymentService } from '@wix/cashier-core/dist/src/services/Payment';
import {
  IQRCodeDisplay,
  IWebPageDisplay,
} from '@wix/cashier-core/dist/src/services/Payment/types';
import { IPaymentMethod } from '@wix/cashier-core/dist/src/services/PaymentMethod/types';
import { CreditCardFormAPI } from '@wix/payments-cc-form/dist/lib/CreditCardForm';
import { CardPublicData } from '@wix/payments-cc-form/dist/src/types/CardPublicData';
import cn from 'classnames';
import { EventEmitter } from 'events';
import isEqual from 'lodash/isEqual';
import React from 'react';
import { IOfflineText } from '../../cashier-payments';
import { BoletoDetails } from '../../types/Boleto';
import { CheckoutStep } from '../../types/CheckoutStep';
import { ErrorCode } from '../../types/ErrorCode';
import { Events } from '../../types/Events';
import { EventType } from '../../types/EventType';
import { Experiment } from '../../types/Experiment';
import { Expiration } from '../../types/Expiration';
import { ExtendedTokenizeResponse } from '../../types/ExtendedTokenizeResponse';
import { InteractionName } from '../../types/InteractionName';
import {
  InternalPaymentsWidgetCallbacks,
  InternalPaymentsWidgetProps,
  InternalPaymentsWidgetPropsConnect,
} from '../../types/InternalPaymentsWidgetProps';
import {
  MFieldName,
  MFieldNamePublic,
  MFieldValuePairs,
} from '../../types/MandatoryFields';
import { PaymentsWidgetAPI } from '../../types/PaymentsWidgetAPI';
import { ExtendedPaymentCompleteResult } from '../../types/PaymentsWidgetProps';
import { Theme } from '../../types/Theme';
import { AsyncJobManager } from '../../utils/AsyncJobManager';
import { getBiLogger, updateBiDefaults } from '../../utils/biLogger';
import { DH } from '../../utils/DataHook';
import {
  captureError,
  captureIfThrows,
  enrichErrorReports,
} from '../../utils/errorReporter';
import { fedopsLogger } from '../../utils/fedopsLogger';
import {
  getFullRenderByInteraction,
  getLoadByInteraction,
  tpasToTrackLoadBy,
} from '../../utils/fedopsLogger/getLoadByInteraction';
import { interactionEndedCrossEnv } from '../../utils/fedopsLogger/interactionEndedCrossEnv';
import { filterDefined } from '../../utils/filterDefined';
import { initI18n, t } from '../../utils/i18n';
import { getParameterByName } from '../../utils/locationUtils';
import { logDeprecationNote } from '../../utils/logDeprecationNote';
import {
  allPossibleMFieldsNames,
  getMFieldShortName,
  isMFieldValueExists,
} from '../../utils/mandatoryFieldsMeta';
import { patchWebpackPublicPath } from '../../utils/patchWebpackPublicPath';
import { perfNow } from '../../utils/perfNow';
import { providersWithoutSaveCCSupport } from '../../utils/providersMaps';
import { wixSdkAdapter } from '../../utils/wixSdkAdapter';
import { CollapsedPayment } from '../CollapsedPayment';
import { Loader } from '../Loader';
import { BoletoModal } from '../modals/BoletoModal';
import { InstantPaymentModal } from '../modals/InstantPaymentModal';
import { InstantPaymentDetails } from '../modals/InstantPaymentModal/types';
import { PayModal } from '../modals/PayModal';
import { SquareVerifyBuyerModal } from '../modals/SquareVerifyBuyerModal';
import {
  requiredSquareMFieldsToVerifyBuyer,
  squareContactFields,
} from '../modals/SquareVerifyBuyerModal/constants';
import { SquareContact } from '../modals/SquareVerifyBuyerModal/types';
import { NoPaymentMethods } from '../NoPaymentMethods';
import { PayButton } from '../PayButton';
import { PaymentDetails } from '../PaymentDetails';
import { PaymentDetailsAPI } from '../PaymentDetails/types';
import { PaymentMethodScreen } from '../PaymentMethodScreen';
import { Offline } from '../PaymentMethodScreen/PaymentMethods/Offline';
import { PaymentMethodsList } from '../PaymentMethodsList';
import { SavedCreditCard } from '../SavedCreditCardsList/types';
import { TermsAndSubmit } from '../TermsAndSubmit';
import { VisibilityWrapper } from '../VisibilityWrapper';
import { maxWidgetReadyTime } from './constants';
import { InitErrorMessage, PaymentErrorMessage } from './elements';
import * as s from './styles.scss';
import {
  ActivePaymentMethod,
  IClientPaymentMethod,
  OptionType,
  PaymentError,
  SelectedOption,
  WidgetJob,
} from './types';
import { filterMethodById } from './utils';
import { buildConfig } from './utils/buildConfig';
import { guardCallbacks } from './utils/guardCallbacks';
import { transformServerToClientPMethods } from './utils/transformServerToClientPMethods';
import { PayModalResponse, PayModalParams } from '../../sdk/types/PayModal';
import { IFetchSettingsResponse, ValidateResult } from '../../sdk/cashier-sdk';
import { paymentsSDK } from '../../sdk/sdk';
import { isAPM } from '../../sdk/utils/isAPM';
import { SquareVerifyBuyerParams } from '../../sdk/types/SquareVerifyBuyerParams';
import { HandlePendingPaymentParams } from '../../sdk/types/HandlePendingPaymentParams';
import { AsyncPaymentResult } from '../../sdk/types/AsyncPaymentResult';
import { PageType } from '../../sdk/types/BILoggerEnums';
import {
  PaymentCompleteResult,
  PaymentFailResult,
} from '../../sdk/types/PaymentCompleteResult';
import {
  isPaymentCompleteResult,
  createPaymentCompleteResultBuilder,
} from '../../sdk/utils/paymentCompleteResult';
import { isSuccessfulServerStatus } from '../../sdk/utils/statusUtils';
import { TCardFieldName } from '@wix/payments-cc-form/dist/src/types/CardFieldName';

interface PaymentsWidgetState {
  isTranslationsLoaded: boolean;
  isMethodsLoaded: boolean;
  isCreditCardsLoaded: boolean;
  isInitFailed: boolean;

  countryCode?: string;
  error?: boolean;
  theme?: string;
  methods?: IClientPaymentMethod[];
  offlineText?: IOfflineText;
  savedCreditCards?: IPublicCreditCard[];
  selectedOption: SelectedOption;
  isMethodCollapsed?: boolean;
  isWalletInstalled?: boolean;
  collapsedMessage?: string;
  collapsedIconKey?: PaymentMethod | CreditCardNetwork;
  isTermsAccepted?: boolean;
  showTermsError?: boolean;
  paymentErrorMessage?: string;
  creditCardIsValid: boolean;
  paymentDetailsIsValid: boolean;
  showingNoPaymentsFirstTime: boolean;
  payModal: {
    visible: boolean;
    paymentMethod?: PaymentMethod;
    url?: string;
    responsive?: boolean;
    onManualClose?(): void;
    onResponse?(response: PayModalResponse): void;
  };
  boletoModal: {
    visible: boolean;
    boleto?: BoletoDetails;
    onClose?(): void;
  };
  instantPaymentModal: {
    visible: boolean;
    paymentDetails?: InstantPaymentDetails;
    onComplete?(paymentResult: AsyncPaymentResult): void;
    onTimeout?(): void;
    onError?(error?: any): void;
    onManualClose?(): void;
  };
  squareVerifyBuyerModal: {
    visible: boolean;
    cardToken?: string;
    onComplete?(buyerVerificationToken?: string): void;
    onError?(): void;
    onManualClose?(): void;
  };
  providedMFieldsTemp: MFieldValuePairs; // should be removed along with "setMandatoryFields" method
}

export default class CashierPaymentsWidget
  extends React.Component<
    InternalPaymentsWidgetPropsConnect,
    PaymentsWidgetState
  >
  implements PaymentsWidgetAPI
{
  static defaultProps: Partial<InternalPaymentsWidgetPropsConnect> = {
    walletPaymentInNewTab: true,
    creditCardPaymentInNewTab: false,
    externalSubmitButton: false,
    submitButtonText: 'button.pay.buyNow',
    termsAndConditionsLink: '',
    useTermsAndConditions: false,
    isSaveCCEnabled: false,
    theme: Theme.accordion,
    showStep: '',
    isWidgetVisible: true,
    allowManualPayment: false,
    allowInPersonPayment: false,
    mandatoryFields: {},
    mandatoryFieldsPrefilled: {},
    // old lazy component doesn't provide fedopsLogger via props, thus need default
    fedopsLogger,
  };

  private _isComponentMounted: boolean = true;
  // used to report initial-load FedOps event only once
  private isInitiallyLoaded: boolean = false;
  private eventEmitter: EventEmitter;
  private clearReadyAwaiter: () => void;
  private fetchSettingsPromise: Promise<IFetchSettingsResponse | void>;
  private paymentDetailsApi: PaymentDetailsAPI;
  private activePaymentMethod?: ActivePaymentMethod;
  private isBIEventsShownWereSent: boolean = false;
  private savedCCLoadingPromise: Promise<any>;
  private jobManager = new AsyncJobManager<WidgetJob>();
  private appInstanceResolver: AppInstanceResolver;
  private cardFormApi: CreditCardFormAPI;

  constructor(props: InternalPaymentsWidgetPropsConnect) {
    super(props);

    props.fedopsLogger.interactionStarted(InteractionName.WIDGET_RENDER);

    enrichErrorReports({
      userId: wixSdkAdapter.getMemberId() || this.config.visitorId,
      locale: this.getLocale(),
      appId: props.configuration.appId,
      msId: this.config.metaSiteId,
      version: this.config.widgetVersion,
    });

    patchWebpackPublicPath(this.config.widgetStaticsBaseUrl);
    this.jobManager.add(WidgetJob.MethodScreenLoad);

    this.appInstanceResolver = new AppInstanceResolver({
      appInstance: this.props.configuration.appInstance,
      revalidateAppInstance: this.props.revalidateAppInstance,
      wixSdkAdapter,
    });

    this.jobManager.add(WidgetJob.ExperimentsLoad, this.initExperiments());
    this.jobManager.add(WidgetJob.LocalizationLoad, this.initLocalization());
    this.eventEmitter = new EventEmitter();

    this.state = {
      isInitFailed: false,
      isMethodsLoaded: false,
      isCreditCardsLoaded: false,
      isTranslationsLoaded: false,
      methods: [],
      theme: props.theme,
      savedCreditCards: [],
      isTermsAccepted: false,
      showTermsError: false,
      selectedOption: {
        type: OptionType.PAYMENT_METHOD,
        value: null,
      },
      creditCardIsValid: false,
      paymentDetailsIsValid: undefined,
      showingNoPaymentsFirstTime: true,
      payModal: { visible: false },
      squareVerifyBuyerModal: { visible: false },
      boletoModal: { visible: false },
      instantPaymentModal: { visible: false },
      providedMFieldsTemp: this.props.mandatoryFields,
    };

    const biDefaults = {
      appId: props.configuration.appId,
      appInstanceId: props.configuration.appInstanceId,
      buyerComponent: 'react',
      visitorId: this.config.visitorId,
      orderSnapshotId: props.configuration.snapshotId,
      msid: this.config.metaSiteId,
    };

    updateBiDefaults(biDefaults);
    getBiLogger().buyerPaymentsComponentLoadStart({ duration: 0 });

    props.fedopsLogger.interactionStarted(InteractionName.INIT_SDK);

    const cashierPaymentsPromise = paymentsSDK
      .init({
        host: this.config.apiBaseUrl,
        preferNewTabOverRedirect: this.config.preferNewTabOverRedirect,
        processingPageUrl:
          this.config.widgetStaticsBaseUrl +
          `assets/pages/payment-processing.html`,
        appId: this.props.configuration?.appId,
        appInstanceId: this.props.configuration?.appInstanceId,
        visitorId: this.config.visitorId,
        siteOwnerId: this.config.siteOwnerId,
        msid: this.props.configuration?.msid,
        snapshotId: this.props.configuration?.snapshotId,
        allowInPersonPayment: props.allowInPersonPayment,
        allowManualPayment: props.allowManualPayment,
        allowRecurringPaymentOnly: props.allowRecurringPaymentOnly,
        query: {
          country: this.props.configuration?.country,
          amount: props.configuration.amount,
          orderId: props.configuration.snapshotId,
          locale: this.getLocale(),
        },
        i18nPromise: this.jobManager.done(WidgetJob.LocalizationLoad),
        revalidateAppInstance: this.revalidateAppInstance,
        onError: captureIfThrows(
          (method: PaymentMethod, result: PaymentError) => {
            if (this.state.isMethodCollapsed) {
              // Stores are not expecting that we'll highlight invalid CC field, which
              // will reject future "initializePayment" call, until User will "expand" the Widget and select the field
              return;
            }

            this.setStateSafe({
              paymentErrorMessage:
                result.error.translatedError || t('payment.submitError'),
            });

            if (result.error.errorCode) {
              this.invalidateFieldByErrorCode(result.error.errorCode);
            }

            this.callbacks.onError?.(method, result);
          }
        ),
        onMessage: captureIfThrows((method, message) => {
          this.callbacks.paymentResult?.({ ...message, method });
        }),
        onPaymentMethodChanged: captureIfThrows(
          (method: PaymentMethod, params) => {
            this.callbacks.paymentMethodChanged?.(method);
            const noBiEvent = params.eventType === EventType.Automatic;
            this.changeActivePaymentMethod(method, noBiEvent);
          }
        ),
        onCreditCardChanged: captureIfThrows(
          (cardId, cardDetails, result, eventType?: string) => {
            this.callbacks.paymentMethodChanged?.(PaymentMethod.CreditCard, {
              creditCardId: cardId,
              cardDetails: filterDefined({
                network: cardDetails.network,
                billingAddress: cardDetails.billingAddress,
                expired: cardDetails.expired,
                expiration: cardDetails.expiration,
                additionalInformation: cardDetails.additionalInformation,
              }),
            });
            this.callbacks.creditCardChanged?.(cardId, cardDetails);
            const noBiEvent = eventType === EventType.Automatic;
            this.changeActiveCreditCard(cardId, cardDetails, noBiEvent);
          }
        ),
        onPaymentStart: captureIfThrows((method: PaymentMethod, params) => {
          const {
            creditCardId,
            isSaveCreditCard,
            chargeId,
            redirectTarget,
            forcedFullRedirectInIAB,
          } = params;
          const methodObj = this.getEnabledPaymentMethods().find(
            filterMethodById(method as PaymentMethod)
          );

          getBiLogger().checkOutSubmitOrder(
            filterDefined({
              creditCardId,
              orderId: this.props.configuration.snapshotId,
              paymentMethodType: method,
              paymentProvider: methodObj?.connectedProviderId,
              saveCreditCard: Boolean(isSaveCreditCard),
              status: true,
              transactionId: chargeId,
              redirectTarget,
              forcedFullRedirectInIAB,
            })
          );
          this.callbacks.onPaymentStart?.(method);
        }),
        onPaymentComplete: captureIfThrows((pMethodName, result) => {
          if (isAPM(pMethodName as PaymentMethod)) {
            this.callbacks.onWalletPaymentComplete?.(result);
          }

          const extendedResult: ExtendedPaymentCompleteResult = {
            ...result,
            status:
              result.clientStatus === 'Approved' ||
              result.clientStatus === 'Pending'
                ? 'OK'
                : result.serverStatus,
            isPending: result.clientStatus === 'Pending',
          };

          this.callbacks.paymentResult?.({
            ...extendedResult,
            method: pMethodName,
          });
          this.callbacks.onPaymentComplete?.(pMethodName, extendedResult);
        }),
        handlePendingPayment: captureIfThrows(this.handlePendingPayment),
        eventEmitter: this.eventEmitter,
        openPayModal: captureIfThrows(this.openPayModal),
        squareVerifyBuyer: captureIfThrows(this.squareVerifyBuyer),
        isSquareVerifyBuyerAvailable: this.isSquareVerifyBuyerAvailable,
      })
      .then(() => {
        props.fedopsLogger.interactionEnded(InteractionName.INIT_SDK);
      });

    this.jobManager.add(WidgetJob.CashierSDKInit, cashierPaymentsPromise);
  }

  componentDidMount() {
    this.reportMount();
    this.returnAPI();
    this.callbacks.onLoadingChanged?.(
      'CashierPaymentsWidget',
      !this.state.isMethodsLoaded
    );
    this.startReadyAwaiter();
    this.jobManager.add(WidgetJob.SettingsLoad, this.fetchSettings());
    this.jobManager.add(WidgetJob.SaveCCInit, this.initSaveCC());

    if (this.props.isWidgetVisible) {
      // TODO: Here will be BI events that should be triggered only when widget becomes visible
      // Maybe, move it to onFullLoad too
      this.eventEmitter.emit(Events.WidgetShown);
      this.onFullLoad().then(this.logBIEventsWhenShown);
    }

    this.onFullLoad().then(this.reportFullLoad);

    if (this.callbacks.onFullLoad) {
      this.onFullLoad().then(this.callbacks.onFullLoad);
    }
  }

  componentDidUpdate(
    prevProps: InternalPaymentsWidgetPropsConnect,
    prevState: PaymentsWidgetState
  ) {
    if (!this.isLoading && !this.isInitiallyLoaded) {
      this.isInitiallyLoaded = true;
      this.reportPartialLoad();
      this.clearReadyAwaiter?.();
    }

    if (prevProps.configuration.country !== this.props.configuration.country) {
      this.updateCountry(this.props.configuration.country);
    } else if (
      !this.props.configuration.country &&
      prevState.countryCode &&
      prevState.countryCode !== this.state.countryCode
    ) {
      // note: changing country in props doesn't affect the widget
      this.updateCountry(this.state.countryCode);
    }

    // Widget becomes visible
    if (this.props.isWidgetVisible && !prevProps.isWidgetVisible) {
      this.eventEmitter.emit(Events.WidgetShown);
    }

    // Here will be BI events that should be triggered only when widget becomes visible
    if (this.props.isWidgetVisible && !prevProps.isWidgetVisible) {
      this.onFullLoad().then(this.logBIEventsWhenShown);
    }

    this.callbacks.onLoadingChanged?.(
      'CashierPaymentsWidget',
      !this.state.isMethodsLoaded
    );
    this.dispatchValidityChange();

    if (this.props.showStep !== prevProps.showStep) {
      this.eventEmitter.emit(Events.WidgetStepChanged, CheckoutStep.OptionView);
    }

    // Pass updated theme to the state and check if it won't override accordion for saved CCs.
    if (this.props.theme !== prevProps.theme) {
      const newTheme = this.state.savedCreditCards.length
        ? Theme.accordion
        : this.props.theme;
      this.setStateSafe({ theme: newTheme });
    }

    // mandatoryFields can be set using two approaches: props and `setMandatoryFields` method
    // thus need to check for both values to not update latest state with initial props
    // (when nextProps.mandatoryFields === this.props.mandatoryFields)
    if (
      !isEqual(this.props.mandatoryFields, prevProps.mandatoryFields) &&
      !isEqual(this.props.mandatoryFields, this.state.providedMFieldsTemp)
    ) {
      this.setStateSafe({
        providedMFieldsTemp: { ...this.props.mandatoryFields },
      });
    }

    // clear cardFormApi ref when form unmounts
    if (
      this.cardFormApi &&
      prevState.selectedOption.value === PaymentMethod.CreditCard &&
      this.state.selectedOption.value !== PaymentMethod.CreditCard
    ) {
      this.cardFormApi = null;
    }

    if (
      this.props.configuration.appInstance !==
      prevProps.configuration.appInstance
    ) {
      this.appInstanceResolver.appInstance =
        this.props.configuration.appInstance;
    }
  }

  componentWillUnmount() {
    this._isComponentMounted = false;
    this.appInstanceResolver.destroy();
    this.clearReadyAwaiter?.();
  }

  private setStateSafe(
    state:
      | ((
          prevState: Readonly<PaymentsWidgetState>,
          props: Readonly<InternalPaymentsWidgetProps>
        ) => Partial<PaymentsWidgetState> | PaymentsWidgetState | null)
      | (Partial<PaymentsWidgetState> | PaymentsWidgetState | null),
    callback?: () => void
  ) {
    if (this._isComponentMounted) {
      this.setState(state as PaymentsWidgetState, callback);
    }
  }

  private get config() {
    return buildConfig(this.props);
  }

  private get callbacks(): InternalPaymentsWidgetCallbacks {
    return guardCallbacks(this.props);
  }

  private returnAPI = () => {
    if (this.callbacks.onApiInit) {
      this.callbacks.onApiInit(this as PaymentsWidgetAPI);
      // old prop
    } else if (this.callbacks.initApi) {
      this.callbacks.initApi(this as PaymentsWidgetAPI);
    }
  };

  private invalidateFieldByErrorCode = (errorCode: number | string) => {
    switch (Number(errorCode)) {
      case ErrorCode.ZipCodeMissing:
        this.paymentDetailsApi.invalidateField('zipCode');
        break;
      case ErrorCode.PhoneNumberMissingOrInvalid:
        this.paymentDetailsApi.invalidateField('phone');
        break;
      case ErrorCode.CardExpired:
      case ErrorCode.InvalidExpirationDate:
        return this.cardFormApi?.focusInvalidField({
          fieldName: `cardExpiration`,
        });
      case ErrorCode.InvalidCardNumber:
      case ErrorCode.CardTypeIsNotSupported:
      case ErrorCode.TestCardDecline:
        return this.cardFormApi?.focusInvalidField({
          fieldName: `cardNumber`,
        });
      case ErrorCode.InvalidCVV:
        return this.cardFormApi?.focusInvalidField({
          fieldName: `cardCvv`,
        });
      default:
    }
  };

  private initLocalization = async (): Promise<void> => {
    return initI18n(this.getLocale())
      .catch((error) => {
        captureError(error?.message);
        this.showInitError(error);
      })
      .then(() => {
        this.setStateSafe({ isTranslationsLoaded: true });
      });
  };

  private initSaveCC = async () => {
    if (this.isSaveCCEnabled() === false) {
      return;
    }

    if (!this.props.isSaveCCEnabled) {
      const isWalletInstalled = await wixSdkAdapter.isWalletAppInstalled();

      this.setStateSafe({ isWalletInstalled });

      if (
        !isWalletInstalled ||
        this.props.configuration.appId === AppId.Cashier
      ) {
        return;
      }
    }

    await this.jobManager.done(WidgetJob.SettingsLoad);

    if (!this.isSavedCCsSupportedByPM()) {
      return;
    }

    this.savedCCLoadingPromise = this.fetchSavedCreditCards();
    this.savedCCLoadingPromise.then(this.setStateCreditCardsCallback);

    return this.savedCCLoadingPromise;
  };

  private openPayModal = (params: PayModalParams) => {
    this.props.fedopsLogger.interactionStarted(InteractionName.PAY_MODAL_ALE);

    this.setStateSafe({
      payModal: {
        visible: true,
        ...params,
        onResponse: (response) => {
          this.closePayModal();
          params.onResponse(response);
        },
      },
    });
  };

  private isSquareVerifyBuyerAvailable = (): {
    isAvailable: boolean;
    missingData?: string[];
  } => {
    try {
      const activePMethod = this.getActivePaymentMethod();
      const missingRequiredProps: string[] = [
        !activePMethod.additionalInfo?.squareAppId && 'squareAppId',
        !activePMethod.additionalInfo?.squareLocationId && 'squareLocationId',
        !this.props.configuration.amount && 'props.configuration.amount',
        !this.props.configuration.currency && 'props.configuration.currency',
        !this.squareBillingContact.givenName && 'firstName',
      ].filter(Boolean);

      if (missingRequiredProps.length === 0) {
        return { isAvailable: true };
      }

      return { isAvailable: false, missingData: missingRequiredProps };
    } catch (e) {
      console.error(
        `Failed to execute "isSquareVerifyBuyerAvailable", treating as not available`,
        e
      );
      return { isAvailable: false };
    }
  };

  private squareVerifyBuyer = (params: SquareVerifyBuyerParams) => {
    this.props.fedopsLogger.interactionStarted(
      InteractionName.SQUARE_3DS_MODAL_MOUNT
    );

    const closeSquareVerifyBuyerModal = () => {
      this.setStateSafe({ squareVerifyBuyerModal: { visible: false } });
    };

    this.setStateSafe({
      squareVerifyBuyerModal: {
        visible: true,
        cardToken: params.cardToken,
        onComplete: (buyerVerificationToken) => {
          closeSquareVerifyBuyerModal();
          params.onComplete(buyerVerificationToken);
        },
        onManualClose: () => {
          closeSquareVerifyBuyerModal();
          params.onManualClose();
        },
        onError: () => {
          closeSquareVerifyBuyerModal();
          params.onError();
        },
      },
    });
  };

  private closePayModal = () => {
    this.setStateSafe({ payModal: { visible: false } });
  };

  private handlePayModalManualClose = () => {
    this.state.payModal.onManualClose();
    this.closePayModal();
  };

  private handlePendingPayment = ({
    paymentMethod,
    chargeResponse,
    onComplete,
    onManualClose,
    onTimeout,
    onError,
  }: HandlePendingPaymentParams) => {
    if (chargeResponse.barcodeDisplay) {
      return this.showBoletoModal({
        boletoDetails: {
          chargeId: chargeResponse?.chargeId,
          orderId: this.props.configuration.snapshotId,
          amount: chargeResponse.amount,
          barcode: chargeResponse.barcodeDisplay.barcode,
          expireDate: chargeResponse.barcodeDisplay.expirationDate,
          email: chargeResponse.barcodeDisplay.billingEmail,
          boletoUrl: chargeResponse.barcodeDisplay.barcodeUrl,
        },
        onComplete,
      });
    }

    if (chargeResponse.qrCodeDisplay || chargeResponse.webPageDisplay) {
      const clientPaymentMethod = this.getEnabledPaymentMethod(paymentMethod);
      const displayData =
        chargeResponse.qrCodeDisplay || chargeResponse.webPageDisplay;
      return this.showInstantPaymentModal({
        paymentDetails: {
          methodIcon: clientPaymentMethod.icon,
          paymentMethod,
          paymentProvider: clientPaymentMethod.connectedProviderId,
          chargeId: chargeResponse.chargeId,
          orderId: this.props.configuration.snapshotId,
          currency: chargeResponse.currency,
          amount: chargeResponse.amount,
          qrCode: {
            payload: (displayData as IQRCodeDisplay)?.payload,
            url: (displayData as IWebPageDisplay)?.url,
          },
          expireDate: displayData.expirationDate,
        },
        onComplete,
        onManualClose,
        onTimeout,
        onError,
      });
    }

    onComplete();
  };

  private showBoletoModal = ({
    boletoDetails,
    onComplete,
  }: {
    boletoDetails: BoletoDetails;
    onComplete(): void;
  }) => {
    this.setStateSafe({
      boletoModal: {
        visible: true,
        boleto: boletoDetails,
        onClose: () => {
          this.setStateSafe({ boletoModal: { visible: false } });
          onComplete();
        },
      },
    });
  };

  private showInstantPaymentModal = ({
    paymentDetails,
    onComplete,
    onManualClose,
    onTimeout,
    onError,
  }: {
    paymentDetails: InstantPaymentDetails;
    onComplete(paymentResult?: AsyncPaymentResult): void;
    onManualClose(): void;
    onTimeout(): void;
    onError(error: any): void;
  }) => {
    this.props.fedopsLogger.interactionStarted(
      InteractionName.INSTANT_PAYMENT_MODAL_MOUNT
    );

    const closeInstantPaymentModal = () => {
      this.setStateSafe({ instantPaymentModal: { visible: false } });
    };

    this.setStateSafe({
      instantPaymentModal: {
        visible: true,
        paymentDetails,
        onManualClose: () => {
          closeInstantPaymentModal();
          onManualClose();
        },
        onTimeout: () => {
          closeInstantPaymentModal();
          onTimeout();
        },
        onComplete: (paymentResult: AsyncPaymentResult) => {
          closeInstantPaymentModal();
          onComplete(paymentResult);
        },
        onError: (error: any) => {
          closeInstantPaymentModal();
          onError(error);
        },
      },
    });
  };

  logBIEventsWhenShown = async () => {
    if (this.isBIEventsShownWereSent) {
      return;
    }
    this.isBIEventsShownWereSent = true;

    interface I180Params {
      orderId: string;
      pageType: string;
      hasSavedCreditCards?: number;
      creditCardId?: string;
      payWith?: string;
      paymentCategory?: string;
      paymentProvider?: string;
    }

    const params: I180Params = {
      orderId: this.props.configuration.snapshotId,
      pageType: PageType.PaymentComponent,
    };

    if (this.savedCCLoadingPromise) {
      const savedCreditCards = await this.savedCCLoadingPromise;
      params.hasSavedCreditCards = savedCreditCards.length;
      const primary =
        savedCreditCards.find((savedCreditCard) => savedCreditCard.primary) ||
        savedCreditCards[0];
      if (primary) {
        params.creditCardId = primary.creditCardId;
      }
    }

    await this.fetchSettingsPromise;
    const paymentCategoryArray = [];
    const paymentProviderArray = [];
    if (this.state.methods.length > 0) {
      this.state.methods.forEach((m) => {
        paymentCategoryArray.push(m.value);
        paymentProviderArray.push(m.connectedProviderId);
      });
      params.paymentCategory = paymentCategoryArray.join(',');
      params.paymentProvider = paymentProviderArray.join(',');
    }

    if (params.creditCardId) {
      params.payWith = PaymentMethod.CreditCard;
    } else if (this.state.methods.length > 0) {
      params.payWith = this.state.methods[0].value;
    } else {
      params.payWith = '';
    }

    getBiLogger().checkoutPaymentComponentShown(params);
  };

  public on = (event: string, handler) => {
    this.eventEmitter.on(event, handler);
  };

  public off = (event: string, handler) => {
    this.eventEmitter.removeListener(event, handler);
  };

  private showInitError = (error?: Error) => {
    this.setStateSafe({ isInitFailed: true });
    this.callbacks.onCrash?.(error);
  };

  private startReadyAwaiter = () => {
    const timeoutHandler = setTimeout(this.showInitError, maxWidgetReadyTime);

    this.clearReadyAwaiter = () => {
      clearTimeout(timeoutHandler);
      this.clearReadyAwaiter = null;
    };
  };

  private get revalidateAppInstance(): RevalidateAppInstance {
    if (!this.appInstanceResolver.revalidate) {
      return;
    }

    return () => {
      getBiLogger().revalidateSessionByWidget({});
      return this.appInstanceResolver.revalidate();
    };
  }

  fetchSavedCreditCards = async () => {
    let savedCreditCards = [];

    this.setStateSafe({ isCreditCardsLoaded: false });

    try {
      this.props.fedopsLogger.interactionStarted(InteractionName.LOAD_SAVED_CC);

      savedCreditCards = await timedOut(
        new CreditCardService({
          baseURL: this.config.apiBaseUrl,
          appInstance: this.appInstanceResolver.appInstance,
          revalidateAppInstance: this.revalidateAppInstance,
        })
          .fetchCreditCards()
          .then((creditCards) => {
            this.props.fedopsLogger.interactionEnded(
              InteractionName.LOAD_SAVED_CC
            );
            return creditCards.sort(
              // @ts-ignore TODO: check actual types on production
              (a: SavedCreditCard, b: SavedCreditCard): number =>
                Number(b.primary) - Number(a.primary)
            );
          }),
        5000,
        () => {
          throw new Error(`Max timeout reached`);
        }
      );
    } catch (error) {
      console.warn("Can't fetch saved credit cards.", error);
    }

    const state = this.aggregateSavedCreditCardsAndCheckTheme(
      { theme: this.state.theme },
      savedCreditCards
    );
    this.setStateSafe({
      ...state,
      isCreditCardsLoaded: true,
    });
    return savedCreditCards;
  };

  fetchSettings = () => {
    this.props.fedopsLogger.interactionStarted(InteractionName.LOAD_SETTINGS);
    const { configuration } = this.props;
    this.setStateSafe({ isMethodsLoaded: false });

    this.fetchSettingsPromise = paymentsSDK
      .fetchSettings({
        ...configuration,
        country: this.state.countryCode,
        cashierAppInstanceId: this.config.cashierAppInstanceId,
        singlePM: this.config.singlePaymentMethod,
        appInstance: this.appInstanceResolver.appInstance,
      })
      .then(async (settings) => {
        this.props.fedopsLogger.interactionEnded(InteractionName.LOAD_SETTINGS);
        await this.jobManager.done(
          WidgetJob.ExperimentsLoad,
          WidgetJob.LocalizationLoad
        );
        this.callbacks.onPaymentMethodsLoaded?.(settings.paymentMethods);

        await new Promise<void>((resolve) => {
          this.setStateSafe(
            {
              isMethodsLoaded: true,
              error: false,
              countryCode: settings.countryCode || '',
              methods: transformServerToClientPMethods(
                settings.paymentMethods,
                {
                  manualPaymentDescription: this.props.manualPaymentDescription,
                }
              ),
            },
            () => {
              this.setStatePaymentsMethodsCallback(settings.paymentMethods);
              resolve();
            }
          );
        });
        return settings;
      })
      .catch((error) => {
        captureError(error, {
          messagePrefix: `Failed to load payment methods`,
          fingerprint: [`pm-load-fail`],
        });
        this.showInitError(error);
        this.callbacks.handleErrorFetchSettings?.();
      });

    return this.fetchSettingsPromise;
  };

  initExperiments = async () => {
    experimentsService.initExperiments(this.config.experimentsInitParams);
    await experimentsService.ready();
  };

  onFullLoad = async (): Promise<void> => {
    await this.jobManager.doneAll();

    if (!this._isComponentMounted) {
      // if component was unmounted before full load - then it wasn't fully loaded (cap)
      return new Promise(() => undefined) as any;
    }
  };

  noAvailablePaymentMethods = () => {
    return this.getEnabledPaymentMethods().length < 1;
  };

  // option = PM | saved CC
  getAvailablePaymentOptionsAmount(): number {
    const availablePaymentMethods: number =
      this.getEnabledPaymentMethods().length;

    if (
      !this.config.singlePaymentMethod &&
      this.getEnabledPaymentMethod(PaymentMethod.CreditCard)
    ) {
      return availablePaymentMethods + this.getEnabledSavedCreditCards().length;
    }

    return availablePaymentMethods;
  }

  getEnabledPaymentMethods = (): IClientPaymentMethod[] => {
    const enabledMethods = this.state.methods.filter(
      ({ disabled }) => !disabled
    );

    if (this.config.singlePaymentMethod) {
      return enabledMethods.filter(
        filterMethodById(this.config.singlePaymentMethod)
      );
    }

    return enabledMethods;
  };

  getEnabledPaymentMethod = (methodId: PaymentMethod): IClientPaymentMethod => {
    return this.getEnabledPaymentMethods().find(filterMethodById(methodId));
  };

  getEnabledSavedCreditCards = () => {
    return this.state.savedCreditCards; // .filter(this.removeDisabled);
  };

  fetchPaymentMethods = (country?: string) => {
    return paymentsSDK.fetchPaymentMethodsByConfiguration(
      this.props.configuration,
      country
    );
  };

  updateCountry = async (country: string) => {
    const { configuration } = this.props;
    this.props.fedopsLogger.interactionStarted(InteractionName.UPDATE_COUNTRY);
    // in case if updateCountry is called before initial fetchSettings is finished it is possible that
    // fetchSettings will finish first and CC form will be rendered -> updateCountry triggers another fetchSettings
    // which will do another SDK.populateMethodFactory -> new SDK CC instance is created, while old instance posses a link
    // to iframe, which will be lost
    // thus need to wait for initial fetchSettings before proceeding
    if (this.fetchSettingsPromise) {
      await this.fetchSettingsPromise;
    }

    // if (!country) {
    //   captureException(new Error('Widget.updateCountry called with undefined'));
    // }
    country = country || this.state.countryCode;

    paymentsSDK.setCountry(country);
    this.setStateSafe({
      isMethodsLoaded: false,
      countryCode: country,
    });

    this.fetchSettingsPromise = paymentsSDK
      .fetchSettings({
        ...configuration,
        country,
        cashierAppInstanceId: this.config.cashierAppInstanceId,
      })
      .then((settings) => {
        this.setStateSafe(
          {
            isMethodsLoaded: true,
            methods: transformServerToClientPMethods(settings.paymentMethods, {
              manualPaymentDescription: this.props.manualPaymentDescription,
            }),
          },
          () => {
            this.props.fedopsLogger.interactionEnded(
              InteractionName.UPDATE_COUNTRY
            );
          }
        );
      })
      .catch(() => {
        this.setStateSafe({ isMethodsLoaded: true }, () =>
          this.props.fedopsLogger.interactionEnded(
            InteractionName.UPDATE_COUNTRY
          )
        );

        this.callbacks.handleErrorFetchSettings?.();
      });
  };

  handleCountryChange = (countryCode: string) => {
    paymentsSDK.setCountry(countryCode);

    if (countryCode && countryCode !== this.state.countryCode) {
      this.setStateSafe({ countryCode });
    }
  };

  updateCreditCardValidity = ({ isValid }: { isValid: boolean }) => {
    if (this.state.creditCardIsValid !== isValid) {
      this.setStateSafe(
        {
          creditCardIsValid: isValid,
        },
        this.dispatchValidityChange
      );
    }
  };

  setPaymentDetailsIsValid = (isValid: boolean) => {
    if (this.state.paymentDetailsIsValid !== isValid) {
      this.setStateSafe(
        {
          paymentDetailsIsValid: isValid,
        },
        this.dispatchValidityChange
      );
    }
  };

  dispatchValidityChange = () => {
    const onValidityChange = this.callbacks.onIsValidChanged;

    if (!onValidityChange) {
      return;
    }

    const paymentMethod = this.getActivePaymentMethod();

    if (!paymentMethod) {
      return onValidityChange(false);
    }

    if (this.isStepVisible(CheckoutStep.OptionView)) {
      if (
        paymentMethod.paymentMethod === PaymentMethod.CreditCard &&
        !this.state.creditCardIsValid
      ) {
        return onValidityChange(false);
      }

      if (!this.state.paymentDetailsIsValid) {
        return onValidityChange(false);
      }

      if (!this.isTermsAccepted()) {
        return onValidityChange(false);
      }
    }

    return onValidityChange(true);
  };

  _getUniqueMandatoryFields = (
    paymentMethods?: IPaymentMethod[]
  ): MFieldNamePublic[] => {
    const methods: (IPaymentMethod | IClientPaymentMethod)[] =
      paymentMethods || this.getEnabledPaymentMethods();
    const allMandatoryFields = methods
      // Gets only methods with mandatory fields provided
      .filter((pm) => !!pm.mandatoryFields)

      // Gets mandatory fields of every payment method
      .map((pm) => {
        if (
          ((pm as IPaymentMethod).paymentMethod ||
            (pm as IClientPaymentMethod).value) === PaymentMethod.CreditCard
        ) {
          const filteredMFields: IMandatoryField[] = pm.mandatoryFields.filter(
            (mField: IMandatoryField) =>
              ![
                MandatoryFieldDomain.CreditCard,
                MandatoryFieldDomain.SavedCreditCard,
              ].includes(mField.domain)
          );

          if (
            pm.connectedProviderId === PaymentProvider.Square &&
            experimentsService.isExperimentEnabled(
              Experiment.EnableSquareVerifyBuyer
            )
          ) {
            // while most Square contact fields are not "mandatory", we need to tell the TPAs that those fields are needed
            // so TPAs will start passing them to us (if they gather them in their form), e.g. Stores won't send us
            // firstName unless we'll tell them it's a mandatory
            filteredMFields.push(
              ...squareContactFields.map((fieldName) => ({
                domain: MandatoryFieldDomain.PaymentMethod,
                fieldName,
              }))
            );
          }

          return filteredMFields;
        }

        return pm.mandatoryFields;
      });

    return (
      // tslint:disable-next-line: prettier
      []// => [{fieldName: 'first'}, {fieldName: 'last'}, {fieldName: 'email'}] // Array flattening: [[{fieldName: 'first'}, {fieldName: 'last'}], [{fieldName: 'email'}]]
      .concat
        .apply([], allMandatoryFields)

        // Mapping to fieldName and its last part after the dot: 'billing.info.address' => 'address'
        .map((m) => getMFieldShortName(m.fieldName))

        // Get unique fields
        .filter((value, index, self) => self.indexOf(value) === index)
    );
  };

  private isCvvForSavedCCRequired(): boolean {
    const creditCardPM = this.state.methods.find(
      ({ value }) => value === PaymentMethod.CreditCard
    );

    return !!creditCardPM?.mandatoryFields.some(
      (mField) =>
        mField.domain === MandatoryFieldDomain.SavedCreditCard &&
        mField.fieldName === 'additional_information.cvv'
    );
  }

  /**
   * This method is fetching all payment methods by country (optionally) to aggregate all needed mandatory fields
   * @param {string} countryCode Country code
   */
  fetchMandatoryFields = (
    countryCode?: string
  ): Promise<MFieldNamePublic[]> => {
    return this.fetchPaymentMethods(countryCode).then((paymentMethodsResult) =>
      this._getUniqueMandatoryFields(paymentMethodsResult.paymentMethods)
    );
  };

  getAllMandatoryFields = (): Promise<MFieldNamePublic[]> => {
    return this.fetchSettingsPromise.then(
      (settingsResponse: IFetchSettingsResponse) =>
        this._getUniqueMandatoryFields(settingsResponse.paymentMethods)
    );
  };

  /**
   * @deprecated Use "mandatoryFields" prop to provide fields values
   */
  setMandatoryFields = (mandatoryFields: MFieldValuePairs) => {
    logDeprecationNote(
      `The proper way to provide Payments Widget with mandatory fields values is via "mandatoryFields" prop. "setMandatoryFields" method will be removed in the future, migrate ASAP`
    );
    this.setStateSafe({
      providedMFieldsTemp: { ...mandatoryFields },
    });
  };

  aggregateSavedCreditCardsAndCheckTheme = (
    state: { theme?: string } = {},
    savedCreditCards = []
  ) => {
    if (savedCreditCards.length && state.theme !== Theme.accordion) {
      state.theme = Theme.accordion;
    }
    return {
      ...state,
      savedCreditCards,
    };
  };

  setStateCreditCardsCallback = async () => {
    this.activePaymentMethod = null;
    if (this.fetchSettingsPromise) {
      await this.fetchSettingsPromise;
    }

    const activeCreditCards = this.getEnabledSavedCreditCards();
    const creditCardIncludedInMethods = Boolean(
      this.getEnabledPaymentMethod(PaymentMethod.CreditCard)
    );

    let firstSavedCC = activeCreditCards.find(({ primary }) => primary);

    if (!firstSavedCC) {
      firstSavedCC = activeCreditCards[0];
    }

    if (firstSavedCC && creditCardIncludedInMethods) {
      const savedCCDetails = {
        billingAddress: firstSavedCC.billingAddress,
        additionalInformation: firstSavedCC.additionalInformation,
        expired: firstSavedCC.expired,
        network: firstSavedCC.network,
        expiration: firstSavedCC.expiration,
      };
      this.onSavedCreditCardChanged(
        firstSavedCC.creditCardId,
        savedCCDetails,
        EventType.Automatic
      );
      return;
    }
  };

  setStatePaymentsMethodsCallback = async (
    paymentMethods: IPaymentMethod[]
  ) => {
    this.activePaymentMethod = null;
    const { selectedOption } = this.state;
    const activeCreditCards = this.getEnabledSavedCreditCards();
    const activePaymentMethods = this.getEnabledPaymentMethods();
    const creditCardIncludedInMethods = activePaymentMethods.some(
      filterMethodById(PaymentMethod.CreditCard)
    );

    let firstSavedCC = activeCreditCards.find(({ primary }) => primary);

    if (!firstSavedCC && activeCreditCards.length) {
      firstSavedCC = activeCreditCards[0];
    }

    if (firstSavedCC && creditCardIncludedInMethods) {
      const savedCCDetails = {
        billingAddress: firstSavedCC.billingAddress,
        additionalInformation: firstSavedCC.additionalInformation,
        expired: firstSavedCC.expired,
        network: firstSavedCC.network,
        expiration: firstSavedCC.expiration,
      };
      this.onSavedCreditCardChanged(
        firstSavedCC.creditCardId,
        savedCCDetails,
        EventType.Automatic
      );
      return;
    }
    const firstPaymentMethod = activePaymentMethods[0];
    const firstPaymentMethodValue = firstPaymentMethod?.value;
    let activeMethod =
      this.props.configuration.defaultPaymentMethod || firstPaymentMethodValue;
    if (
      selectedOption.type === OptionType.PAYMENT_METHOD &&
      paymentMethods &&
      paymentMethods
        .filter(({ disabled }) => !disabled)
        .find((pm) => pm.paymentMethod === selectedOption.value)
    ) {
      activeMethod = selectedOption.value;
    }
    if (activeMethod) {
      paymentsSDK.setActivePaymentMethod(activeMethod, EventType.Automatic);
    }
  };

  handleTermsStatusChange = (e) => {
    this.setStateSafe({
      isTermsAccepted: !this.state.isTermsAccepted,
      showTermsError: false,
    });
  };

  getLocale() {
    return (
      this.props.configuration.locale ||
      this.props.locale ||
      getParameterByName('locale', window.location.href) ||
      'en'
    );
  }

  isStepVisible(step: string) {
    return this.props.showStep === '' || this.props.showStep === step;
  }

  getActivePaymentMethod = (): ActivePaymentMethod => {
    const { selectedOption } = this.state;
    const isSavedCreditCard =
      selectedOption.type === OptionType.SAVED_CREDIT_CARD;
    const paymentMethodValue: PaymentMethod = selectedOption.value;
    const paymentMethod = this.getEnabledPaymentMethod(paymentMethodValue);
    if (!paymentMethod) {
      return null;
    }

    const isNeedToUpdate =
      !this.activePaymentMethod ||
      (isSavedCreditCard &&
        this.activePaymentMethod.selectedOption.payload?.creditCardId !==
          selectedOption.payload?.creditCardId) ||
      this.activePaymentMethod.paymentMethod !== paymentMethod.value ||
      // https://jira.wixpress.com/browse/PAY-2464
      (this.activePaymentMethod.additionalInfo &&
        paymentMethod.additionalInfo &&
        this.activePaymentMethod.additionalInfo.installments !==
          paymentMethod.additionalInfo.installments);

    if (isNeedToUpdate) {
      this.activePaymentMethod = {
        ...paymentMethod.serverPMethod,
        paymentMethod: paymentMethod.value,
        name: paymentMethod.label,
        selectedOption,
        connectedProviderId: paymentMethod.connectedProviderId,
        iconUrl: paymentMethod.icon,
        additionalInfo: paymentMethod.additionalInfo || {},
        frameable: paymentMethod.frameable,
        responsive: paymentMethod.responsive,
        redirectable: paymentMethod.redirectable,
      };
    }

    return this.activePaymentMethod;
  };

  getSelectedMethod = (): IClientPaymentMethod => {
    return this.state.methods.find(
      ({ value }) => value === this.state.selectedOption.value
    );
  };

  getSelectedMethodMFields = (): IMandatoryField[] => {
    return this.getSelectedMethod()?.mandatoryFields || [];
  };

  renderPaymentError() {
    return (
      <PaymentErrorMessage
        message={this.state.paymentErrorMessage}
        isModalTheme={this.props.theme === Theme.modal}
      />
    );
  }

  renderInitError() {
    return <InitErrorMessage isModalTheme={this.props.theme === Theme.modal} />;
  }

  renderMethodScreen() {
    const paymentMethod = this.getActivePaymentMethod();
    const { selectedOption } = this.state;
    const isSavedCreditCardSelected =
      selectedOption.type === OptionType.SAVED_CREDIT_CARD;

    if (!paymentMethod) {
      return null;
    }

    return (
      <VisibilityWrapper visible={!this.state.isMethodCollapsed} name={'pm'}>
        <PaymentMethodScreen
          locale={this.getLocale()}
          msid={this.config.metaSiteId}
          appInstance={this.appInstanceResolver.appInstance}
          visitorId={this.config.visitorId}
          siteOwnerId={this.config.siteOwnerId}
          paymentsSDK={paymentsSDK}
          paymentMethod={this.getActivePaymentMethod()}
          isSaveCCEnabled={this.isSaveCCEnabled()}
          isSinglePaymentOption={this.getAvailablePaymentOptionsAmount() === 1}
          tokenizeMode={this.props.tokenizeMode}
          ccFormUrlOverride={this.config.overrides.ccFormUrl}
          onLoadingChanged={this.callbacks.onLoadingChanged}
          onFullLoad={() => this.jobManager.finish(WidgetJob.MethodScreenLoad)}
          savedCard={
            isSavedCreditCardSelected
              ? {
                  id: selectedOption.payload.creditCardId,
                  network: selectedOption.payload.cardDetails.network,
                  isCVVRequired: this.isCvvForSavedCCRequired(),
                  isExpired: selectedOption.payload.cardDetails.expired,
                  expiration: selectedOption.payload.cardDetails.expiration,
                }
              : null
          }
          theme={this.state.theme as Theme}
          countryCode={this.state.countryCode}
          shouldApplySiteStyles={this.props.shouldApplySiteStyles}
          isWalletWithTitle={
            this.getAvailablePaymentOptionsAmount() === 1 ||
            this.state.theme === Theme.modal
          }
          onCardFormApiInit={(api) => {
            this.cardFormApi = api;
            paymentsSDK.setCardFormApi(api);
          }}
          onCardFieldChange={this.updateCreditCardValidity}
          revalidateAppInstance={this.revalidateAppInstance}
        />
      </VisibilityWrapper>
    );
  }

  renderCollapsedMethodScreen() {
    const activePM = this.getActivePaymentMethod();
    const isCreditCardMethod =
      activePM.paymentMethod === PaymentMethod.CreditCard;

    return (
      <VisibilityWrapper
        display={this.state.isMethodCollapsed}
        name={'collapsed-pm'}
      >
        <CollapsedPayment
          withIcon={!!this.state.collapsedIconKey}
          paymentMethod={activePM.paymentMethod as PaymentMethod}
          creditCardType={
            isCreditCardMethod
              ? (this.state.collapsedIconKey as CreditCardNetwork)
              : null
          }
          iconUrl={activePM.iconUrl}
          title={this.state.collapsedMessage || '●●●● ●●●● ●●●● ●●●●'}
        />
      </VisibilityWrapper>
    );
  }

  renderCollapsedOfflineMethod() {
    const method = this.getActivePaymentMethod();

    return (
      <Offline
        text={method.additionalInfo?.text}
        theme={this.state.theme as Theme}
        isSinglePaymentOption={this.getAvailablePaymentOptionsAmount() === 1}
      />
    );
  }

  renderMFieldsForm() {
    if (this.noAvailablePaymentMethods() || !this.getActivePaymentMethod()) {
      this.callbacks.onLoadingChanged?.('PaymentDetails', false);
      return null;
    }

    let mFieldsMeta = this.getSelectedMethodMFields();

    if (this.props.tokenizeMode) {
      if (
        this.getSelectedMethod().connectedProviderId ===
          PaymentProvider.Square &&
        experimentsService.isExperimentEnabled(
          Experiment.EnableSquareVerifyBuyer
        )
      ) {
        mFieldsMeta = mFieldsMeta.filter((mField) =>
          requiredSquareMFieldsToVerifyBuyer.includes(
            getMFieldShortName(mField.fieldName)
          )
        );
      } else {
        return null;
      }
    }

    return (
      <PaymentDetails
        host={this.props.host}
        theme={this.props.theme as Theme}
        deviceType={this.props.deviceType}
        countryCode={this.state.countryCode}
        locale={this.getLocale()}
        orderId={this.props.configuration.snapshotId}
        paymentMethod={
          this.getActivePaymentMethod().paymentMethod as PaymentMethod
        }
        mFieldsMeta={mFieldsMeta}
        mandatoryFields={this.state.providedMFieldsTemp}
        mandatoryFieldsPrefilled={this.props.mandatoryFieldsPrefilled}
        isCollapsed={this.state.isMethodCollapsed}
        onIsValidChanged={this.setPaymentDetailsIsValid}
        shouldApplySiteStyles={this.props.shouldApplySiteStyles}
        onCountryChange={this.handleCountryChange}
        onApiInit={(api) => (this.paymentDetailsApi = api)}
      />
    );
  }

  renderSubmitArea() {
    return (
      <div>
        <VisibilityWrapper
          display={this.props.useTermsAndConditions}
          name={'terms'}
        >
          <TermsAndSubmit
            termsAndConditionsLink={this.props.termsAndConditionsLink}
            handleTermsStatusChange={this.handleTermsStatusChange}
            showError={!this.isTermsAccepted() && this.state.showTermsError}
            checked={this.state.isTermsAccepted}
          />
        </VisibilityWrapper>
        <VisibilityWrapper
          display={!this.props.externalSubmitButton}
          name={'pay-button'}
        >
          <div
            style={{
              marginTop:
                this.state.theme === Theme.modal
                  ? 0
                  : this.props.deviceType === 'mobile'
                  ? 35
                  : 48,
            }}
          >
            <PayButton
              text={this.props.submitButtonText}
              isDisabled={!this.isTermsAccepted()}
              pay={this.pay}
              onLoadingChanged={this.callbacks.onLoadingChanged}
            />
          </div>
        </VisibilityWrapper>
      </div>
    );
  }

  handleNoPaymentsRefreshClick = () => {
    this.setStateSafe({
      showingNoPaymentsFirstTime: false,
    });

    if (this.isSaveCCEnabled()) {
      this.savedCCLoadingPromise = this.fetchSavedCreditCards();
    }

    return this.fetchSettings();
  };

  isSavedCCsSupportedByPM(): boolean | undefined {
    if (this.state.isMethodsLoaded) {
      const cardPM = this.getEnabledPaymentMethod(PaymentMethod.CreditCard);

      return (
        !!cardPM &&
        !providersWithoutSaveCCSupport.includes(cardPM?.connectedProviderId)
      );
    }
  }

  isSaveCCEnabled(): boolean | undefined {
    if (
      this.config.singlePaymentMethod ||
      this.props.tokenizeMode ||
      !this.config.isLiveSiteMode ||
      !this.config.isUserSignedIn
    ) {
      return false;
    }

    if (!this.props.isSaveCCEnabled) {
      if (this.state.isWalletInstalled === undefined) {
        return;
      }

      if (
        this.state.isWalletInstalled &&
        this.props.configuration.appId === AppId.Cashier
      ) {
        return false;
      }

      if (!this.state.isWalletInstalled) {
        return false;
      }
    }

    if (this.isSavedCCsSupportedByPM() === undefined) {
      return;
    }

    if (!this.isSavedCCsSupportedByPM()) {
      return false;
    }

    return true;
  }

  private getAvailableMFields = (): {
    [mFieldName in MFieldName]?: string;
  } => {
    const mFieldsSources: MFieldValuePairs[] = [];
    const mFieldsRequiredByPM = this.paymentDetailsApi.getFields();

    mFieldsSources.push(
      mFieldsRequiredByPM,
      // not all of the fields are required by the PSP (they are not "mandatory fields"), as result
      // they'll be missing in the map returned from `getFields()` (contains only "mandatory fields")
      // but we might get the missing values from the maps passed by TPA (they don't filter by "mandatory fields" and
      // pass everything they gathered)
      this.state.providedMFieldsTemp,
      this.props.mandatoryFieldsPrefilled
    );

    const getMFieldValue = (mFieldName: MFieldName): string =>
      mFieldsSources.find((mFieldsSource) =>
        mFieldsSource.hasOwnProperty(mFieldName)
      )?.[mFieldName];

    return Object.fromEntries(
      allPossibleMFieldsNames
        .map((mFieldName) => [mFieldName, getMFieldValue(mFieldName)])
        .filter(([, value]) => isMFieldValueExists(value))
    ) as { [mFieldName in MFieldName]?: string };
  };

  get squareBillingContact(): SquareContact {
    const mFields = this.getAvailableMFields();

    return filterDefined({
      givenName: mFields.firstName,
      familyName: mFields.lastName,
      email: mFields.email,
      city: mFields.city,
      region: mFields.state,
      phone: mFields.phone,
      ...(mFields.countryCode && {
        country: alpha3ToAlpha2(mFields.countryCode),
      }),
      postalCode: mFields.zipCode,
      ...(mFields.address && {
        addressLines: [mFields.address],
      }),
    });
  }

  renderNoPaymentMethods() {
    return (
      <VisibilityWrapper
        display={this.noAvailablePaymentMethods()}
        name={'no-pms'}
      >
        <NoPaymentMethods
          showingNoPaymentsFirstTime={this.state.showingNoPaymentsFirstTime}
          paymentMethods={this.state.methods}
          onRefreshClick={this.handleNoPaymentsRefreshClick}
        />
      </VisibilityWrapper>
    );
  }

  renderOptionsList() {
    const enabledPaymentMethods = this.getEnabledPaymentMethods();
    const creditCardIncludedInMethods = enabledPaymentMethods.some(
      filterMethodById(PaymentMethod.CreditCard)
    );
    const shouldRenderSavedCreditCards: boolean =
      this.isSaveCCEnabled() &&
      creditCardIncludedInMethods &&
      this.state.savedCreditCards.length > 0;

    return (
      <VisibilityWrapper
        visible={
          !this.state.isMethodCollapsed &&
          this.isStepVisible(CheckoutStep.OptionsList)
        }
        name={'pm-list'}
      >
        <PaymentMethodsList
          paymentMethods={enabledPaymentMethods}
          theme={this.state.theme as Theme}
          selectedMethod={
            this.state.selectedOption.type === OptionType.PAYMENT_METHOD
              ? this.state.selectedOption.value
              : null
          }
          onPaymentMethodChanged={this.onPaymentMethodChanged}
          onSavedCreditCardChanged={this.onSavedCreditCardChanged}
          creditCards={
            shouldRenderSavedCreditCards
              ? this.getEnabledSavedCreditCards()
              : []
          }
          selectedCardId={
            this.state.selectedOption.type === OptionType.SAVED_CREDIT_CARD
              ? this.state.selectedOption.payload.creditCardId
              : null
          }
          activePaymentComponentNode={
            this.state.theme === Theme.accordion && this.renderMethodScreen()
          }
          locale={this.getLocale()}
        />
      </VisibilityWrapper>
    );
  }

  renderPayModal() {
    let width, height;

    if (this.state.payModal.responsive) {
      width = 440;
      height = 600;
    } else {
      // TODO: revise nonResponsive modal sizes
      width = 440;
      height = 600;
    }
    return (
      <PayModal
        url={this.state.payModal.url}
        paymentMethod={this.state.payModal.paymentMethod}
        publicBaseUrl={this.config.widgetStaticsBaseUrl}
        width={width}
        height={height}
        locale={this.getLocale()}
        userId={this.config.visitorId}
        widgetVersion={this.config.widgetVersion}
        fedopsLogger={this.props.fedopsLogger}
        onManualClose={this.handlePayModalManualClose}
        onResponse={this.state.payModal.onResponse}
      />
    );
  }

  renderBoletoModal() {
    return (
      <BoletoModal
        deviceType={this.props.deviceType}
        boleto={this.state.boletoModal.boleto}
        onClose={this.state.boletoModal.onClose}
      />
    );
  }

  renderInstantPaymentModal() {
    return (
      <InstantPaymentModal
        publicBaseUrl={this.config.widgetStaticsBaseUrl}
        appInstance={this.appInstanceResolver.appInstance}
        locale={this.getLocale()}
        userId={this.config.visitorId}
        fedopsLogger={this.props.fedopsLogger}
        paymentDetails={this.state.instantPaymentModal.paymentDetails}
        onComplete={this.state.instantPaymentModal.onComplete}
        onTimeout={this.state.instantPaymentModal.onTimeout}
        onManualClose={this.state.instantPaymentModal.onManualClose}
        onError={this.state.instantPaymentModal.onError}
        revalidateAppInstance={this.revalidateAppInstance}
      />
    );
  }

  renderSquareVerifyBuyerModal() {
    const { additionalInfo } = this.getActivePaymentMethod();

    return (
      <SquareVerifyBuyerModal
        squareAppId={additionalInfo.squareAppId}
        squareLocationId={additionalInfo.squareLocationId}
        cardToken={this.state.squareVerifyBuyerModal.cardToken}
        verificationDetails={{
          amount: this.props.configuration.amount,
          currencyCode: this.props.configuration.currency,
          billingContact: this.squareBillingContact,
        }}
        locale={this.getLocale()}
        publicBaseUrl={this.config.widgetStaticsBaseUrl}
        userId={this.config.visitorId}
        widgetVersion={this.config.widgetVersion}
        fedopsLogger={this.props.fedopsLogger}
        onComplete={this.state.squareVerifyBuyerModal.onComplete}
        onError={this.state.squareVerifyBuyerModal.onError}
        onManualClose={this.state.squareVerifyBuyerModal.onManualClose}
      />
    );
  }

  private onPaymentMethodChanged = (pmId: string) => {
    paymentsSDK.setActivePaymentMethod(pmId, EventType.Manual);
  };

  private onSavedCreditCardChanged = (
    ccId: string,
    ccDetails?: {},
    eventType?: string
  ) => {
    paymentsSDK.setActiveCreditCard(ccId, ccDetails, eventType);
  };

  private changeActivePaymentMethod = (
    pmId: PaymentMethod,
    noBiEvent?: boolean
  ) => {
    if (!noBiEvent) {
      getBiLogger().buyerChoosesPaymentMethod({
        paymentCategory: pmId,
        pageType: PageType.PaymentComponent,
      });
    }
    this.setStateSafe({
      selectedOption: {
        type: OptionType.PAYMENT_METHOD,
        value: pmId,
      },
      isMethodCollapsed: false,
      collapsedMessage: '',
      paymentErrorMessage: '',
      creditCardIsValid: false, //method changed, so not matter is current method CC or not - CC not valid after method change
    });
  };

  private changeActiveCreditCard = (
    creditCardId: string,
    cardDetails: {
      billingAddress?: any;
      additionalInformation?: any;
      network?: CreditCardNetwork;
      expired?: boolean;
      expiration?: Expiration;
    } = {
      expired: false,
      additionalInformation: {},
      billingAddress: {},
    },
    noBiEvent?: boolean
  ) => {
    if (!noBiEvent) {
      getBiLogger().buyerChoosesPaymentMethod({
        creditCardId,
        paymentCategory: PaymentMethod.CreditCard,
        pageType: PageType.PaymentComponent,
      });
    }
    this.setStateSafe({
      selectedOption: {
        type: OptionType.SAVED_CREDIT_CARD,
        value: PaymentMethod.CreditCard,
        payload: { creditCardId, cardDetails },
      },
      isMethodCollapsed: false,
      collapsedMessage: '',
      paymentErrorMessage: '',
    });
  };

  private reportValidationStatus = (validateResult: ValidateResult): void => {
    try {
      const invalidFields = [];
      const paymentMethod = this.getSelectedMethod();

      if (!validateResult.isValid) {
        invalidFields.push(
          ...this.paymentDetailsApi.getInvalidFields(),
          ...(validateResult.invalidFields || [])
        );
      }

      paymentsSDK.logMandatoryFieldsValidationStatus({
        invalidFields,
        status: validateResult.isValid,
        paymentMethod: paymentMethod.value,
        paymentProvider: paymentMethod.connectedProviderId,
        orderId: this.props.configuration.snapshotId,
        pageType: PageType.PaymentComponent,
        buyerComponent: 'react',
      });
    } catch (e) {
      console.error('Widget.reportValidationStatus error', e);
    }
  };

  private reportInteractionEndedCrossEnv = (
    interactionName: InteractionName
  ) => {
    if (this.props.fedopsMeta) {
      if (this.props.fedopsMeta.isLoggerPassedThrough) {
        this.props.fedopsLogger.interactionEnded(interactionName);
      } else {
        interactionEndedCrossEnv<InteractionName>({
          logger: this.props.fedopsLogger,
          interactionName,
          startTime: this.props.fedopsMeta.startLoadTime,
          endTime: perfNow(),
        });
      }
    }
  };

  private reportMount = () => {
    this.reportInteractionEndedCrossEnv(InteractionName.ALE_MOUNT);
    this.props.fedopsLogger.interactionStarted(InteractionName.ALE_RENDER);
  };

  // should be fired on first meaningful widget render (e.g. cc-form might still be loading, while PMs are shown)
  private reportPartialLoad = () => {
    getBiLogger().paymentsComponentLoadComplete({
      ...(this.props.fedopsMeta?.startLoadTime && {
        duration: Math.round(perfNow() - this.props.fedopsMeta.startLoadTime),
      }),
      status: true,
    });

    if (this.props.fedopsMeta?.isLoadInteractionStarted) {
      this.reportInteractionEndedCrossEnv(InteractionName.WIDGET_LOAD);
    }

    this.props.fedopsLogger.interactionEnded(InteractionName.ALE_RENDER);

    if (this.props.configuration?.appId === AppId.Restaurants) {
      this.props.fedopsLogger.interactionStarted(
        getFullRenderByInteraction(this.props.configuration?.appId)
      );
      // for yet unknown reasons in Restaurants there is an issue with lower success rate for last phase of load
      // that doesn't reproduce manually/in tests/no complaints
      // thus treating Widget as fully loaded on partial load (cc-form might still be loading)
      this.reportInteractionEndedCrossEnv(InteractionName.ALE);
    } else {
      this.props.fedopsLogger.interactionStarted(
        InteractionName.ALE_FULL_RENDER
      );
    }

    if (tpasToTrackLoadBy.includes(this.props.configuration?.appId)) {
      this.props.fedopsLogger.interactionEnded(
        getLoadByInteraction(this.props.configuration?.appId)
      );
    }

    this.props.fedopsLogger.interactionEnded(InteractionName.WIDGET_RENDER);
  };

  public reportFullLoad = () => {
    if (this.props.configuration?.appId === AppId.Restaurants) {
      this.props.fedopsLogger.interactionEnded(
        getFullRenderByInteraction(this.props.configuration?.appId)
      );
    } else {
      this.reportInteractionEndedCrossEnv(InteractionName.ALE);
      this.props.fedopsLogger.interactionEnded(InteractionName.ALE_FULL_RENDER);
    }
  };

  private reportCollapse() {
    const { methods, selectedOption } = this.state;
    const paymentMethod = selectedOption.value;
    const creditCardPM = methods.find(
      ({ value }) => value === PaymentMethod.CreditCard
    );

    getBiLogger().checkoutPaymentComponentCollapsed({
      paymentCategory: paymentMethod,
      ...(paymentMethod === PaymentMethod.CreditCard &&
        !creditCardPM.disabled && {
          paymentProvider: creditCardPM.connectedProviderId,
          saveCreditCard:
            selectedOption.type !== OptionType.SAVED_CREDIT_CARD &&
            !!this.props.isSaveCardChecked,
          ...(selectedOption.type === OptionType.SAVED_CREDIT_CARD && {
            creditCardId: selectedOption.payload.creditCardId,
          }),
        }),
    });
  }

  public tokenize = async (): Promise<ExtendedTokenizeResponse> => {
    if (!this.props.tokenizeMode) {
      throw new Error(
        `"tokenize" is available only in tokenizeMode. Pass "tokenizeMode" prop`
      );
    }

    const validateResult = await this.validate();

    if (!validateResult.isValid) {
      throw validateResult;
    }

    const tokenizeResponse: ExtendedTokenizeResponse =
      await paymentsSDK.tokenize({
        instance: this.appInstanceResolver.appInstance,
        providerId: this.getSelectedMethod().connectedProviderId,
      });

    if (
      this.getSelectedMethod().connectedProviderId === PaymentProvider.Square &&
      experimentsService.isExperimentEnabled(Experiment.EnableSquareVerifyBuyer)
    ) {
      const mFields = this.getAvailableMFields();

      if (mFields.firstName && mFields.lastName) {
        tokenizeResponse.firstName = mFields.firstName;
        tokenizeResponse.lastName = mFields.lastName;
      }
    }

    return tokenizeResponse;
  };

  public continuePayment = async (
    responseToken: string,
    { inNewTab } = { inNewTab: this.config.preferNewTabOverRedirect }
  ): Promise<PaymentCompleteResult> => {
    let selectedPaymentMethod: IClientPaymentMethod;

    try {
      const paymentMethod = this.state.selectedOption.value;
      const { isMerchantContext } = this.props.configuration;

      if (this.props.tokenizeMode) {
        throw await paymentsSDK.createPaymentFailResult({
          paymentMethod,
          isMerchantContext,
          error: {
            errorMessage: `"continuePayment" is unavailable in tokenizeMode`,
          },
        });
      }

      selectedPaymentMethod = this.getSelectedMethod();
      const {
        frameable,
        responsive,
        label: methodTitle,
      } = selectedPaymentMethod;

      return await paymentsSDK.continuePayment(paymentMethod, {
        methodTitle,
        responseToken,
        inNewTab,
        saveCreditCard: this.isSaveCCEnabled() && this.props.isSaveCardChecked,
        frameable,
        responsive,
        isMerchantContext,
        mFieldsFlat: this.paymentDetailsApi.getFields(),
        instance: this.appInstanceResolver.appInstance,
      });
    } catch (error) {
      if (
        !isPaymentCompleteResult(error) ||
        (error as PaymentFailResult).isUnexpectedFailure
      ) {
        captureError(error, {
          messagePrefix: `Failed to continuePayment`,
          fingerprint: ['continuePayment-fail'],
        });

        const chargeResponse = PaymentService.parseResponseToken(responseToken);

        const isUnresolvedInstantPayment = (): boolean =>
          chargeResponse.qrCodeDisplay &&
          chargeResponse.transactionStatus ===
            ServerTransactionStatus.PendingBuyer;

        if (
          isSuccessfulServerStatus(chargeResponse.transactionStatus) &&
          // in case of instant payment and "PendingBuyer" status the payment can't be considered successful
          // (buyer for sure didn't have a chance to pay yet)
          !isUnresolvedInstantPayment()
        ) {
          // Exception was caught during handling successful Charge
          // given that Charge was successful we don't want to propagate our internal Exception
          // (which would be treated by consuming App as Failed to Pay)
          return createPaymentCompleteResultBuilder()
            .withPaymentMethod(selectedPaymentMethod?.value as PaymentMethod)
            .withChargeResponse(chargeResponse)
            .build();
        }
      }

      throw error;
    }
  };

  public isTermsAccepted() {
    return !this.props.useTermsAndConditions || this.state.isTermsAccepted;
  }

  public get isLoading(): boolean {
    return (
      !this.state.isMethodsLoaded ||
      !this.state.isTranslationsLoaded ||
      this.isSaveCCEnabled() === undefined ||
      (this.isSaveCCEnabled() && !this.state.isCreditCardsLoaded)
    );
  }

  public localizeError = (errorCode: string): Promise<string> =>
    paymentsSDK.localizeError(
      errorCode,
      this.props.configuration.isMerchantContext
    );

  /**
   * @deprecated Use "pay" instead
   */
  public submit = () => {
    logDeprecationNote(`"submit" method is deprecated. Use "pay" instead`);
    return this.pay();
  };

  /**
   * @deprecated Use "cancelPayment" instead
   */
  public cancelPaymentPopup = async (): Promise<void> => {
    logDeprecationNote(
      `"cancelPaymentPopup" method is deprecated. Use "cancelPayment" instead`
    );
    await this.cancelPayment();
  };

  public prepareToPay = async (): Promise<void> => {
    await paymentsSDK.prepareToPay({
      pMethodData: this.getSelectedMethod().serverPMethod,
    });
  };

  public cancelPayment = async (): Promise<void> => {
    // TODO: implement canceling payment when Modal is shown
    await paymentsSDK.cancelPayment();
  };

  public pay = async (params?: {
    orderId?: string;
  }): Promise<PaymentCompleteResult> => {
    // TODO: create some sort of "paymentInProgress" object and pass it through the flow, so that it'll be possible
    // to stop the payment if it was cancelled in the middle of the flow (e.g. before making /charge)
    const paymentMethod = this.state.selectedOption.value;
    const { isMerchantContext } = this.props.configuration;

    if (this.props.tokenizeMode) {
      throw await paymentsSDK.createPaymentFailResult({
        paymentMethod,
        isMerchantContext,
        error: {
          errorMessage: `"pay" is unavailable in tokenizeMode`,
        },
      });
    }

    const result = await this.validate();

    this.reportValidationStatus(result);

    if (!result.isValid) {
      throw result;
    }

    this.setStateSafe({
      paymentErrorMessage: '',
    });

    const {
      label: methodTitle,
      frameable,
      responsive,
      redirectable,
      connectedProviderId,
    } = this.getSelectedMethod();

    return paymentsSDK.pay(paymentMethod, {
      orderId: params?.orderId,
      instance: this.appInstanceResolver.appInstance,
      saveCreditCard: this.isSaveCCEnabled() && this.props.isSaveCardChecked,
      creditCardId: this.state.selectedOption.payload?.creditCardId,
      methodTitle,
      frameable,
      responsive,
      redirectable,
      isMerchantContext,
      mFieldsFlat: this.paymentDetailsApi.getFields(),
      installments: this.props.cardInstallments,
      providerId: connectedProviderId as PaymentProvider,
    });
  };

  public validate = async (): Promise<ValidateResult> => {
    try {
      const result: ValidateResult = {
        isValid: true,
        invalidFields: [],
      };

      if (!this.isTermsAccepted()) {
        this.setStateSafe({ showTermsError: true });

        result.isValid = false;
        result.invalidFields.push('termsAndConditions');
      }

      const methodValidateResult: ValidateResult = await paymentsSDK.validate();

      if (methodValidateResult) {
        result.isValid = result.isValid && methodValidateResult.isValid;
        result.invalidFields.push(
          ...(methodValidateResult.invalidFields || [])
        );
        if (
          this.state.selectedOption.value === PaymentMethod.CreditCard &&
          methodValidateResult.invalidFields?.length > 0
        ) {
          await this.cardFormApi?.focusInvalidField({
            fieldName: methodValidateResult.invalidFields[0] as TCardFieldName,
          });
        }
      }

      if (this.paymentDetailsApi) {
        const mFieldsValidateResult: ValidateResult =
          this.paymentDetailsApi.validate();

        if (mFieldsValidateResult) {
          result.isValid = result.isValid && mFieldsValidateResult.isValid;
          result.invalidFields.push(
            ...(mFieldsValidateResult.invalidFields || [])
          );

          // focus first invalid mandatory field (ignore if creditCard form is not valid)
          if (
            methodValidateResult.isValid &&
            mFieldsValidateResult.invalidFields?.length > 0
          ) {
            this.paymentDetailsApi.invalidateField(
              mFieldsValidateResult.invalidFields[0] as MFieldName
            );
          }
        }
      }

      return result;
    } catch (err) {
      captureError(err, {
        messagePrefix: `Failed to validate user input`,
        fingerprint: ['validate-fail'],
      });

      return {
        isValid: false,
        invalidFields: [],
      };
    }
  };

  private getCardPublicData = async (
    initialCardPublicData?: CardPublicData
  ): Promise<CardPublicData> => {
    const { selectedOption } = this.state;

    if (selectedOption.value !== PaymentMethod.CreditCard) {
      throw new Error(
        `getCardPublicData can be called only if selected PM is CC`
      );
    }

    const cardPublicData: CardPublicData =
      initialCardPublicData || (await paymentsSDK.getCreditCardPublicData());

    if (selectedOption.type === OptionType.SAVED_CREDIT_CARD) {
      const selectedSavedCard = this.getEnabledSavedCreditCards().find(
        ({ creditCardId }) =>
          creditCardId === selectedOption.payload.creditCardId
      );

      if (selectedSavedCard) {
        cardPublicData.firstSixDigits = selectedSavedCard.firstSixDigits;
        cardPublicData.lastFourDigits = selectedSavedCard.lastFourDigits;
        cardPublicData.type = selectedSavedCard.network;

        if (!cardPublicData.expiration) {
          cardPublicData.expiration = selectedSavedCard.expiration;
        }
      }
    }

    return cardPublicData;
  };

  public expand = () => {
    this.setStateSafe({ isMethodCollapsed: false });
    return paymentsSDK.expand();
  };

  public collapse = async (): Promise<any> => {
    const validationResult = await this.validate();

    if (!validationResult.isValid) {
      throw validationResult;
    }

    const collapseResult: CardPublicData | any = await paymentsSDK.collapse();
    const { selectedOption } = this.state;
    const selectedPaymentMethod: PaymentMethod = selectedOption.value;
    const activePM: ActivePaymentMethod = this.getActivePaymentMethod();
    let collapsedMessage;
    let collapsedIconKey: PaymentMethod | CreditCardNetwork;

    if (selectedPaymentMethod === PaymentMethod.CreditCard) {
      try {
        const cardPublicData: CardPublicData = await this.getCardPublicData(
          collapseResult
        );

        collapsedMessage = `●●●● ●●●● ●●●● ${cardPublicData.lastFourDigits}`;
        collapsedIconKey = cardPublicData.type as CreditCardNetwork;
      } catch (e) {
        captureError(e, {
          messagePrefix: `Failed to get card public data`,
          fingerprint: ['get-card-public-data-fail'],
        });
        collapsedMessage = `●●●● ●●●● ●●●● ●●●●`;
        collapsedIconKey = PaymentMethod.CreditCard;
      }
    } else if (selectedPaymentMethod === PaymentMethod.Offline) {
      collapsedMessage = this.renderCollapsedOfflineMethod();
    } else {
      collapsedMessage = (
        <span>
          {t('payments.collapsedMode.label', {
            paymentMethodLabel: activePM.methodTitle,
          })}{' '}
        </span>
      );
      collapsedIconKey = selectedPaymentMethod;
    }

    this.setStateSafe(
      {
        isMethodCollapsed: true,
        collapsedMessage,
        collapsedIconKey,
      },
      () => {
        this.reportCollapse();
      }
    );

    return collapseResult;
  };

  public initializePayment = async () => {
    const { selectedOption } = this.state;
    const paymentMethod = selectedOption.value;
    const { isMerchantContext } = this.props.configuration;

    if (this.props.tokenizeMode) {
      throw await paymentsSDK.createPaymentFailResult({
        paymentMethod,
        isMerchantContext,
        error: {
          errorMessage: `"initializePayment" is unavailable in tokenizeMode`,
        },
      });
    }

    const { connectedProviderId } = this.getSelectedMethod();
    let cardDetails: Parameters<typeof paymentsSDK.initializePayment>[2] = {
      providerId: connectedProviderId as PaymentProvider,
    };

    if (
      this.isSaveCCEnabled() &&
      selectedOption.type === OptionType.SAVED_CREDIT_CARD
    ) {
      cardDetails = {
        ...cardDetails,
        ...selectedOption.payload.cardDetails,
        creditCardId: selectedOption.payload.creditCardId,
      };
    }
    const { frameable, redirectable } = this.getSelectedMethod();

    const { detailsId } = await paymentsSDK.initializePayment(
      selectedOption.value,
      undefined,
      cardDetails,
      {
        frameable,
        redirectable,
        instance: this.appInstanceResolver.appInstance,
        isMerchantContext,
        mFieldsFlat: this.paymentDetailsApi.getFields(),
        installments: this.props.cardInstallments,
        saveCreditCard: this.isSaveCCEnabled() && this.props.isSaveCardChecked,
      }
    );

    const result: {
      detailsId: string;
      cardPublicData?: CardPublicData;
    } = {
      detailsId,
    };

    if (selectedOption.value === PaymentMethod.CreditCard) {
      result.cardPublicData = await this.getCardPublicData();
    }

    return result;
  };

  renderContent = () => {
    const { showStep, tokenizeMode } = this.props;
    const isDetailsStepHidden: boolean = !this.isStepVisible(
      CheckoutStep.OptionView
    );

    return (
      <div
        data-hook={DH.ContentWrapper}
        className={cn(this.isLoading && s.hidden)}
        hidden={this.isLoading ? true : null}
      >
        {!this.state.isMethodCollapsed &&
          this.state.paymentErrorMessage &&
          this.renderPaymentError()}
        {!tokenizeMode &&
          (showStep || this.getAvailablePaymentOptionsAmount() > 1) &&
          this.renderOptionsList()}
        <div
          data-hook="wcn-step-paymentDetails"
          className={cn(isDetailsStepHidden && s.hide)}
          hidden={isDetailsStepHidden}
          aria-hidden={isDetailsStepHidden}
        >
          {(this.state.theme !== Theme.accordion ||
            this.getAvailablePaymentOptionsAmount() === 1) &&
            this.renderMethodScreen()}
          {this.state.isMethodCollapsed && this.renderCollapsedMethodScreen()}
          {this.renderMFieldsForm()}
          {!tokenizeMode &&
            !this.state.isMethodCollapsed &&
            !this.noAvailablePaymentMethods() &&
            this.renderSubmitArea()}
          {this.renderNoPaymentMethods()}
        </div>
      </div>
    );
  };

  renderLoader = () => (
    <div data-hook={DH.MainLoader} className={s.loaderWrap}>
      <Loader />
    </div>
  );

  renderModal() {
    if (this.state.payModal.visible) {
      return this.renderPayModal();
    }

    if (this.state.boletoModal.visible) {
      return this.renderBoletoModal();
    }

    if (this.state.instantPaymentModal.visible) {
      return this.renderInstantPaymentModal();
    }

    if (this.state.squareVerifyBuyerModal.visible) {
      return this.renderSquareVerifyBuyerModal();
    }
  }

  render() {
    if (this.state.isInitFailed) {
      return this.renderInitError();
    }

    const isRTL: boolean = isRTLLocale(this.getLocale());

    return (
      <div
        data-hook={DH.Root}
        className={s.paymentsWidget}
        style={{ maxWidth: this.props.maxWidth }}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {this.isLoading && this.renderLoader()}
        {this.state.isTranslationsLoaded && this.renderContent()}
        {this.renderModal()}
      </div>
    );
  }
}
